/* ==========================================================================
   SUNDRY — theme.js
   Small, dependency-free, progressively enhanced.
   Nothing here is required for the page to render or be usable; every
   feature layers on top of working HTML.
   ========================================================================== */
(function () {
  "use strict";

  var root = document.documentElement;
  root.classList.remove("no-js");
  root.classList.add("js");

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Shared by every focus trap in this file — the drawer and the search panel.
  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  // Cycles Tab inside `container` so focus can't wander behind an open overlay.
  function trapTab(e, container) {
    if (e.key !== "Tab") return;
    var items = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* --- Localised strings + polite announcements -------------------------- */
  var STRINGS = {};
  try {
    var stringsEl = document.querySelector("[data-theme-strings]");
    if (stringsEl) STRINGS = JSON.parse(stringsEl.textContent);
  } catch (e) { }

  function t(key) { return STRINGS[key] || ""; }

  // Screen readers get no signal from a label swap, so say it out loud.
  function announce(message) {
    var region = document.querySelector("[data-live-region]");
    if (!region || !message) return;
    region.textContent = "";
    // Re-setting after a tick guarantees the change is registered.
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  /* --- Scroll reveal ----------------------------------------------------- */
  function initReveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll("[data-reveal]"));
    if (!els.length) return;
    var show = function (el) { el.classList.add("is-visible"); };
    if (reduceMotion || !("IntersectionObserver" in window)) {
      els.forEach(show);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { show(e.target); io.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px" });
    els.forEach(function (el) { io.observe(el); });
    // Safety net: never leave content hidden.
    setTimeout(function () { els.forEach(show); }, 2600);
  }

  /* --- Image fade-in ----------------------------------------------------- */
  function initImageFade() {
    document.querySelectorAll("img.fade-img:not(.is-loaded)").forEach(function (img) {
      var show = function () { img.classList.add("is-loaded"); };
      if (img.complete) { show(); return; }
      img.addEventListener("load", show, { once: true });
      // A broken image must not sit invisible forever — show its alt text.
      img.addEventListener("error", show, { once: true });
    });
  }

  /* --- Mobile navigation drawer ----------------------------------------- */
  function initDrawer() {
    var drawer = document.querySelector("[data-drawer]");
    // Re-runnable: the Theme Editor replaces the whole header on every setting
    // change, which leaves the new burger and drawer unbound. The guard means
    // this can sit in enhance() without double-binding an untouched drawer.
    if (!drawer || drawer.dataset.bound === "true") return;
    drawer.dataset.bound = "true";
    var openBtns = document.querySelectorAll("[data-drawer-open]");
    var closeEls = drawer.querySelectorAll("[data-drawer-close]");
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      openBtns.forEach(function (b) { b.setAttribute("aria-expanded", "true"); });
      document.body.style.overflow = "hidden";
      var first = drawer.querySelector(FOCUSABLE);
      if (first) first.focus();
      document.addEventListener("keydown", onKey);
    }
    function close() {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      openBtns.forEach(function (b) { b.setAttribute("aria-expanded", "false"); });
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      if (lastFocus) lastFocus.focus();
    }
    // Escape closes; Tab cycles inside the panel so focus can't wander behind it.
    function onKey(e) {
      if (e.key === "Escape") { close(); return; }
      trapTab(e, drawer);
    }

    openBtns.forEach(function (b) { b.addEventListener("click", open); });
    closeEls.forEach(function (b) { b.addEventListener("click", close); });
  }

  /* --- Header search panel ----------------------------------------------- */
  // The pill in the header is a real link to the search page. This upgrades it
  // into a disclosure for an in-page panel with live product results; if this
  // never runs, the link still works and the panel stays hidden.
  //
  // Guarded like the drawer: the Theme Editor replaces the whole header — and
  // the overlay inside it — on every setting change, which would otherwise
  // leave the new nodes unbound or bind the old ones twice.
  function initSearch() {
    var overlay = document.querySelector("[data-search-overlay]");
    if (!overlay || overlay.dataset.bound === "true") return;

    var trigger = document.querySelector("[data-search-open]");
    var input = overlay.querySelector("[data-search-input]");
    var results = overlay.querySelector("[data-search-results]");
    var endpoint = overlay.getAttribute("data-search-url");
    if (!trigger || !input || !results || !endpoint) return;

    overlay.dataset.bound = "true";

    var MIN_CHARS = 2;
    var DEBOUNCE_MS = 250;
    var timer = null;
    var controller = null;

    // Only now does the link behave like a disclosure, so only now does it
    // claim to be one.
    trigger.setAttribute("aria-controls", overlay.id);
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-haspopup", "dialog");

    function open(e) {
      if (e) e.preventDefault();
      overlay.hidden = false;
      trigger.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
      // Synchronously, still inside the click handler: iOS Safari only raises
      // the keyboard for a focus() that happens within the user gesture, and
      // deferring to rAF would additionally stall in a throttled background tab.
      input.focus();
      document.addEventListener("keydown", onKey);
    }

    function close() {
      if (overlay.hidden) return;
      window.clearTimeout(timer);
      if (controller) { controller.abort(); controller = null; }
      overlay.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      // The trigger, not whatever held focus when the panel opened: clicking a
      // link doesn't focus it in every browser, so that would drop focus back
      // to <body> and lose the user's place in the page.
      trigger.focus();
    }

    function onKey(e) {
      if (e.key === "Escape") { close(); return; }
      trapTab(e, overlay);
    }

    function render(html) {
      // The section arrives wrapped in its own Shopify container; unwrap it so
      // the panel isn't nesting a section shell on every keystroke.
      var doc = new DOMParser().parseFromString(html, "text/html");
      var section = doc.querySelector("#shopify-section-predictive-search");
      results.innerHTML = section ? section.innerHTML : "";
      initImageFade();
    }

    function search(term) {
      // Abort whatever is still in flight, or a slow early response can land
      // after a fast later one and show results for a stale term.
      if (controller) controller.abort();
      controller = new AbortController();

      var url = endpoint +
        "?q=" + encodeURIComponent(term) +
        "&resources[type]=product" +
        "&resources[limit]=4" +
        "&section_id=predictive-search";

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error(r.status)); })
        .then(render)
        .catch(function (err) {
          // An abort is the normal path on every keystroke, not a failure.
          // Anything else (predictive search off, network down) leaves the panel
          // as a plain form that still submits to the search page.
          if (err && err.name === "AbortError") return;
          results.innerHTML = "";
        });
    }

    trigger.addEventListener("click", open);

    overlay.querySelectorAll("[data-search-close]").forEach(function (el) {
      el.addEventListener("click", close);
    });

    input.addEventListener("input", function () {
      var term = input.value.trim();
      window.clearTimeout(timer);
      if (term.length < MIN_CHARS) {
        if (controller) { controller.abort(); controller = null; }
        results.innerHTML = "";
        return;
      }
      timer = window.setTimeout(function () { search(term); }, DEBOUNCE_MS);
    });
  }

  /* --- Account menu ------------------------------------------------------ */
  // The <details> element already opens, closes, and reports aria-expanded on
  // its own. This adds only what it doesn't do: dismiss on outside click, on
  // Escape, and on tabbing out. If this never runs, the menu still works.
  //
  // Deliberately not a hover menu \u2014 hover menus are unreachable on touch and
  // fire by accident on the way to the cart beside them.
  // Delegated from document and bound once, on purpose. It looks the menu up at
  // event time rather than at bind time, so it cannot be defeated by the menu
  // not existing yet, by the Theme Editor replacing the header, or by an
  // earlier initialiser throwing before this one is reached.
  function bindAccountMenu() {
    function openMenus() {
      return document.querySelectorAll("details[data-account][open]");
    }
    function closeAll(refocus) {
      openMenus().forEach(function (menu) {
        menu.open = false;
        if (!refocus) return;
        var summary = menu.querySelector("summary");
        if (summary) summary.focus();
      });
    }

    // Clicking the summary toggles natively. That click still bubbles here, but
    // <details> flips `open` in its default action — after propagation — so the
    // menu still reads as closed at this point and this won't undo the opening.
    document.addEventListener("click", function (e) {
      openMenus().forEach(function (menu) {
        if (!menu.contains(e.target)) menu.open = false;
      });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") { closeAll(true); return; }
      if (e.key !== "Tab") return;
      // Let focus land first, then see where it went.
      window.setTimeout(function () {
        openMenus().forEach(function (menu) {
          if (!menu.contains(document.activeElement)) menu.open = false;
        });
      }, 0);
    });
  }

  /* --- Maker's mark (touch-device autoplay) ------------------------------ */
  // Hover already discovers the reveal on desktop/laptop pointers; on touch
  // devices, autoplay it briefly when the mark scrolls into view instead.
  function initWatermark() {
    var marks = document.querySelectorAll("[data-watermark]");
    if (!marks.length) return;
    var isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    if (!isTouch || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        e.target.classList.toggle("is-visible", e.isIntersecting);
      });
    }, { threshold: 0.6 });
    marks.forEach(function (mark) { io.observe(mark); });
  }

  /* --- AJAX quick add ---------------------------------------------------- */
  function initQuickAdd() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-quick-add]");
      if (!btn) return;
      var variantId = btn.getAttribute("data-quick-add");
      if (!variantId) return; // no default variant → let the product link handle it
      e.preventDefault();
      addToCart(variantId, btn);
    });
  }
  function addToCart(variantId, btn) {
    var label = btn.querySelector("[data-qa-label]");
    var original = label ? label.textContent : "";
    btn.disabled = true;
    if (label) label.textContent = t("adding");
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart/add.js" : "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; }, function () { throw {}; });
        return r.json();
      })
      .then(function () {
        if (label) label.textContent = t("added");
        announce(t("added"));
        updateCartCount();
        document.dispatchEvent(new CustomEvent("sundry:cart:added"));
        setTimeout(function () { if (label) label.textContent = original; btn.disabled = false; }, 1400);
      })
      .catch(function (err) {
        // Shopify explains why in `description`; passing it on beats "Try again".
        var reason = (err && (err.description || err.message)) || t("add_failed");
        if (label) label.textContent = t("add_failed");
        announce(reason);
        setTimeout(function () { if (label) label.textContent = original; btn.disabled = false; }, 1400);
      });
  }
  function updateCartCount() {
    fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/") + "cart.js", {
      headers: { Accept: "application/json" }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll("[data-cart-count]").forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () { });
  }

  /* --- Boot -------------------------------------------------------------- */
  // Everything in this file is optional polish, so one enhancer failing must
  // not take the rest down with it — without this, a throw in an early
  // initialiser silently disables every feature after it. The warning names the
  // culprit instead of leaving a dead page and no explanation.
  function run(fn) {
    try {
      fn();
    } catch (err) {
      if (window.console && console.warn) console.warn("[sundry] " + fn.name + " failed:", err);
    }
  }

  // Safe to re-run over markup that appeared after boot. The delegated
  // listeners are deliberately not here — they bind once and already cover
  // nodes added later.
  function enhance() {
    [initReveal, initImageFade, initDrawer, initSearch, initWatermark].forEach(run);
  }

  function boot() {
    [initQuickAdd, bindAccountMenu].forEach(run); // delegated, once only
    enhance();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-run enhancers when a section is re-rendered in the Theme Editor.
  document.addEventListener("shopify:section:load", enhance);
})();
